import axios from 'axios';
import { notification, Statistic } from 'antd';
import { sessionClear } from './functionTool';
import { GetDecryptedString } from './encryption';
import i18n from '../i18n';
const { Countdown } = Statistic;

// 創建新的axios實例
const customAxios = axios.create();

const logout = () => {
  const currentPath = window.location.pathname;
  if (!currentPath.includes('/login') && !currentPath.includes('/auth')) {
    sessionStorage.setItem('redirectPath', currentPath);
  }
  notification.warning({
    message: (
      <div>
        {i18n.t('common.axios.loginExpiredPrefix')}
        <Countdown
          value={Date.now() + 1000}
          format="s"
          onFinish={() => (window.location.href = '/login')}
          style={{ display: 'inline-block', padding: '0 2px' }}
          className="logout-count-down"
        />
        {i18n.t('common.axios.loginExpiredSuffix')}
      </div>
    ),
    placement: 'top',
    key: '401',
  });
  sessionClear();
};

// 將攔截器應用到自定義的axios實例
customAxios.interceptors.request.use(
    (config) => {
      const token = GetDecryptedString(localStorage.getItem('token'));
      if (token) {
        config.headers['Authorization'] = token;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  

// 將response攔截器應用到自定義的axios實例
customAxios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.code === 'ERR_NETWORK') {
        // 檢查是否為檔案上傳請求
        const isFileUpload = error.config?.headers?.['Content-Type']?.includes('multipart/form-data');
        const isUploadEndpoint = error.config?.url?.includes('/upload_file');
        
        if (isFileUpload && isUploadEndpoint) {
          console.warn('檔案上傳失敗，可能是檔案過大');
          notification.warning({
            message: <div>{i18n.t('common.axios.fileTooLarge')}</div>,
            placement: 'top',
            key: 'file-too-large',
            duration: 0,
            closeIcon: true,
          });
        } else {
          console.warn('網路連線錯誤');
          notification.warning({
            message: <div>{i18n.t('common.axios.networkError')}</div>,
            placement: 'top',
            key: 'network-error',
          });
        }
        return Promise.reject(error);
      }

      if (error.response) {
        const rawDetail = error?.response?.data?.detail;
        // 後端可能回傳結構化的 detail 物件（帶 code 供前端在地化），
        // 此處的字串比對只適用於純文字 detail。
        const detail = typeof rawDetail === 'string' ? rawDetail : '';
        const hasStructuredDetail = !!rawDetail && typeof rawDetail === 'object';
        switch (error.response.status) {
          case 400:
            if (detail?.includes('pageToken')) {
              console.warn('pageToken error');
            } else if (detail?.toLowerCase()?.includes('not authenticated')) {
              console.warn('Not Authenticated!');
              logout();
            } else if (hasStructuredDetail) {
              // 具體錯誤訊息由呼叫端依 code 在地化後顯示，避免重複提示
              console.warn('Request rejected:', rawDetail.code);
            } else {
              console.warn('Google Drive API error');
              notification.warning({
                message: <div>{i18n.t('common.axios.invalidRequest')}</div>,
                placement: 'top',
                key: '400',
              });
            }
            break;
          case 401:
            console.warn('端點請求超時或無權限');
            logout();
            break;
          case 403:
            console.warn('沒有存取權限');
            // 如果後端返回了具體的錯誤訊息（如 scope 相關錯誤），不顯示通用訊息
            // 讓呼叫端自行處理，避免重複顯示錯誤
            if (!detail || detail === 'Forbidden') {
              notification.warning({
                message: <div>{i18n.t('common.axios.accessDenied')}</div>,
                placement: 'top',
                key: '403',
              });
            }
            // 具體錯誤訊息由呼叫端處理
            break;
          case 404:
            if (detail?.includes('Not Authenticated!')) {
              console.warn('Not Authenticated!');
              logout();
            } else if (detail?.includes('File not found') && detail?.includes('drive/v3/files')) {
              console.warn('Google Drive 檔案權限錯誤');
              notification.warning({
                message: <div>{i18n.t('common.axios.driveFileError')}</div>,
                placement: 'top',
                key: '404-drive',
                duration: 0,
              });
            } else if (detail?.includes('找不到事件')) {
              console.warn('找不到事件');
            } else {
              console.warn('找不到該頁面');
              notification.warning({
                message: <div>{i18n.t('common.axios.pageNotFound')}</div>,
                placement: 'top',
                key: '404',
              });
            }
            break;
          case 413:
            console.warn('上傳檔案過大');
            notification.warning({
              message: <div>{i18n.t('common.axios.fileUploadTooLarge')}</div>,
              placement: 'top',
              key: '413',
            });
            break;
          
          case 500:
            const errorDetails = error.response.data.detail || '';
            const isPageTokenError = errorDetails?.includes('pageToken');
            if (isPageTokenError) {
              console.warn('pageToken error');
            } else {
              console.warn('伺服器出錯');
              notification.warning({
                message: <div>{i18n.t('common.axios.serverError')}</div>,
                placement: 'top',
                key: '500',
              });
            }
            break;
          case 503:
            console.warn('服務失效');
            notification.warning({
              message: <div>{i18n.t('common.axios.serviceUnavailable')}</div>,
              placement: 'top',
              key: '503',
            });
            break;
          case 504:
            console.warn('伺服器逾時');
            notification.warning({
              message: <div>{i18n.t('common.axios.serverTimeout')}</div>,
              placement: 'top',
              key: '504',
            });
            break;
          default:
            console.warn(`連接錯誤: ${error.response.status}`);
        }
      }
      // 確保總是拒絕promise，傳遞錯誤以便後續處理
      return Promise.reject(error);
    }
  );

// 導出自定義的axios實例，而不是修改全局的axios
export default customAxios;
// 如果需要原始的axios，可以這樣導出
export { axios as originalAxios }; 